import type { GameState, CommandResult, LogEntry, InventoryItem } from '../types/game';
import { FISH_SPECIES, getFishById } from './fish';
import { getLocationDefById } from './locations';
import { calcBonus } from './skills';
import { getRandomBycatch, rollForBycatch } from './bycatch';
import { makeEntry } from '../ui/format';
import { FISH_XP_BASE, FISH_XP_PER_TIER } from './constants';

/**
 * Generate a fish catch using the full rarity/weight calculation pipeline.
 * Returns a zero-weight sardine if no fish are available at the current location.
 */
export function generateFishCatch(state: GameState): { type: 'fish'; fishId: string; weight: number } {
  const location = getLocationDefById(state.currentLocation)!;

  const rodLevel = state.equipment.find(e => e.id === 'rod')?.level ?? 1;
  const baitLevel = state.equipment.find(e => e.id === 'bait')?.level ?? 1;
  const lineLevel = state.equipment.find(e => e.id === 'line')?.level ?? 1;
  const fishingSkill = state.playerStats.skills.fishing.level;
  const luckSkill = state.playerStats.skills.luck.level;

  const rodWeightBonus = calcBonus(rodLevel, 0.112);
  const lineWeightBonus = calcBonus(lineLevel, 0.07);
  const fishingWeightBonus = calcBonus(fishingSkill, 0.07);
  const baitRarityBonus = calcBonus(baitLevel, 0.084);
  const luckRarityBonus = calcBonus(luckSkill, 0.056);

  const availableFish = FISH_SPECIES.filter(f => location.fishTier >= f.minLocationTier);

  if (availableFish.length === 0) {
    return { type: 'fish', fishId: 'sardine', weight: 0 };
  }

  let totalWeight = 0;
  for (const fish of availableFish) {
    let modRarity = fish.rarity;
    if (location.id !== fish.preferredLocation) {
      modRarity *= 0.3;
    } else {
      modRarity *= 1.5;
    }
    modRarity *= location.fishModifier;
    modRarity *= baitRarityBonus * luckRarityBonus;
    totalWeight += modRarity;
  }

  let roll = Math.random() * totalWeight;
  let caught = availableFish[0];
  for (const fish of availableFish) {
    let modRarity = fish.rarity;
    if (location.id !== fish.preferredLocation) {
      modRarity *= 0.3;
    } else {
      modRarity *= 1.5;
    }
    modRarity *= location.fishModifier;
    modRarity *= baitRarityBonus * luckRarityBonus;
    roll -= modRarity;
    if (roll <= 0) {
      caught = fish;
      break;
    }
  }

  const baseWeight = caught.minWeight + Math.random() * (caught.maxWeight - caught.minWeight);
  const weightMult = rodWeightBonus * lineWeightBonus * fishingWeightBonus;
  const finalWeight = Math.round(baseWeight * weightMult * 100) / 100;

  return { type: 'fish', fishId: caught.id, weight: finalWeight };
}

/** Generate a single catch — fish (with rarity/weight) or bycatch based on location chance. */
export function generateSingleCatch(state: GameState): { item: InventoryItem; logText: string } {
  const location = getLocationDefById(state.currentLocation);

  if (location && rollForBycatch(location.bycatchChance)) {
    const bycatch = getRandomBycatch();
    return {
      item: { type: 'bycatch', bycatchId: bycatch.id },
      logText: `Found ${bycatch.name} (${bycatch.category})!`,
    };
  }

  const item = generateFishCatch(state);
  if (item.weight === 0) {
    return { item, logText: 'You waited... but nothing bit.' };
  }

  const species = getFishById(item.fishId);
  return {
    item,
    logText: `Caught a ${species?.name || item.fishId} (${item.weight}kg)!`,
  };
}

/**
 * Resolve the current fishing operation.
 * Multi-catch infrastructure is in place but always produces 1 catch for now.
 */
export function resolveFish(state: GameState): CommandResult {
  const location = getLocationDefById(state.currentLocation);
  if (!location || !state.isFishing) {
    return { logEntries: [], statePatch: { isFishing: false, fishingStartTime: null } };
  }

  const catchCount = 1;
  const items: InventoryItem[] = [];
  const logEntries: LogEntry[] = [];

  for (let i = 0; i < catchCount; i++) {
    const { item, logText } = generateSingleCatch(state);
    items.push(item);

    if (item.type === 'fish' && item.weight === 0) {
      logEntries.push(makeEntry(logText, 'info'));
      break;
    }
    logEntries.push(makeEntry(logText, 'success'));
  }

  const newInventory = [...state.inventory, ...items];

  return {
    logEntries,
    statePatch: {
      inventory: newInventory,
      fishCaught: state.fishCaught + items.filter(i => i.type === 'fish').length,
      isFishing: false,
      fishingStartTime: null,
    },
  };
}

/** Calculate the fish XP reward based on current location tier. */
export function getFishXp(tier: number): number {
  return FISH_XP_BASE + tier * FISH_XP_PER_TIER;
}
